import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  Divider,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { T } from '../admin/adminTheme';
import {
  AvailabilityChip,
  BoolChip,
  FilterableSortableTableHead,
  SectionCard,
  StyledTableRow,
  TabHeader,
  TableStateWrapper,
  applyColumnFilters,
  useSortableTable,
} from '../admin/shared';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const COLUMNS = [
  { id: 'EmpID', label: 'Emp ID', minWidth: 100 },
  { id: 'Name', label: 'Name', minWidth: 180 },
  { id: 'MachineGroup', label: 'Group', minWidth: 140 },
  { id: 'Designation', label: 'Designation', minWidth: 130 },
  { id: 'ActiveStatus', label: 'Status', minWidth: 120 },
  { id: 'avaliability', label: 'Availability', minWidth: 130 },
  { id: 'activeMachineCode', label: 'Machine', minWidth: 120 },
  { id: 'hasUserAccount', label: 'User Account', minWidth: 130 },
  { id: 'User_Category', label: 'User Role', minWidth: 110 },
];

export default function MasterEmployeesView({ supervisorId, onSnack }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const load = useCallback(async () => {
    if (!supervisorId) return;

    setLoading(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/machinesupervisor/master/employees/${supervisorId}`
      );

      setList(Array.isArray(res.data) ? res.data : []);
      setColumnFilters({});
    } catch (err) {
      onSnack?.(
        err?.response?.data?.message || 'Failed to load allocated employees.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [onSnack, supervisorId]);

  useEffect(() => {
    load();
  }, [load]);

  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((employee) => (
      String(employee.EmpID).toLowerCase().includes(q) ||
      employee.Name?.toLowerCase().includes(q) ||
      employee.MachineGroup?.toLowerCase().includes(q) ||
      employee.Designation?.toLowerCase().includes(q) ||
      String(employee.LinkedUserID || '').toLowerCase().includes(q) ||
      employee.User_Category?.toLowerCase().includes(q)
    ));
  }, [list, search]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters),
    [searchFiltered, columnFilters]
  );

  const {
    sortedRows: rows,
    sortField,
    sortDir,
    handleSort,
  } = useSortableTable(columnFilteredRows, 'EmpID');

  return (
    <Fade in>
      <Box>
        <SectionCard>
          <TabHeader
            count={rows.length}
            noun="Allocated Employees"
            search={search}
            onSearch={setSearch}
            onRefresh={load}
            hideAdd
          />

          <Divider />

          <TableStateWrapper
            loading={loading}
            empty={rows.length === 0}
            emptyMessage={search ? 'No allocated employees match your search.' : 'No allocated employees found.'}
            colSpan={COLUMNS.length}
            minHeight={360}
            skeletonRows={7}
          >
            <TableContainer>
              <Table>
                <FilterableSortableTableHead
                  columns={COLUMNS}
                  sourceRows={searchFiltered}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                />

                <TableBody>
                  {rows.map((employee) => {
                    const hasUserAccount = Number(employee.hasUserAccount) === 1;

                    return (
                      <StyledTableRow key={employee.EmpID}>
                        <TableCell sx={{ fontWeight: 800, fontFamily: 'monospace', color: t.heading }}>
                          {employee.EmpID}
                        </TableCell>

                        <TableCell>{employee.Name || '—'}</TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.primary }}>
                            {employee.MachineGroup || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>{employee.Designation || '—'}</TableCell>

                        <TableCell>
                          <BoolChip value={employee.ActiveStatus} />
                        </TableCell>

                        <TableCell>
                          <AvailabilityChip value={employee.avaliability} />
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontFamily: 'monospace',
                              fontWeight: employee.activeMachineCode ? 700 : 400,
                              color: employee.activeMachineCode ? t.primary : t.muted,
                            }}
                          >
                            {employee.activeMachineCode || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={hasUserAccount ? 'Linked' : 'Missing'}
                            color={hasUserAccount ? 'success' : 'warning'}
                          />
                        </TableCell>

                        <TableCell>
                          {employee.User_Category ? (
                            <Chip size="small" label={employee.User_Category} variant="outlined" />
                          ) : (
                            <Typography sx={{ fontSize: 12, color: t.muted }}>—</Typography>
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TableStateWrapper>
        </SectionCard>
      </Box>
    </Fade>
  );
}
